import { Modal, Form, message } from 'antd'
import {
    ProForm,
    ProFormText,
    ProFormSelect,
    ProFormDependency,
} from '@ant-design/pro-components'
import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import styles from './add-channel.module.scss'
import './add-channel.module.scss'
import whatsappIcon from '@/assets/images/whatsapp.png'
import telegramIcon from '@/assets/images/telegram.png'
import livechatIcon from '@/assets/images/livechat.png'
import lineIcon from '@/assets/images/line.png'
import { createTelegramChannelApi } from '@/service/channel/api/createTelegramChannel'
import { createWebwidgetChannelApi } from '@/service/channel/api/createWebwidgetChannel'
import { createLineChannelApi } from '@/service/channel/api/createLineChannel'
import { createWaAccount, genWaChannelId } from '@/service/whatsappPersonal/api'

const channelTypeOptions = [
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={telegramIcon} alt="Telegram" style={{ width: 20, height: 20 }} />Telegram</span>, value: 'telegram' },
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={livechatIcon} alt="WebWidget" style={{ width: 20, height: 20 }} />WebWidget</span>, value: 'webwidget' },
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={whatsappIcon} alt="WhatsApp" style={{ width: 20, height: 20 }} />WhatsApp</span>, value: 'whatsapp', disabled: true },
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={whatsappIcon} alt="WhatsAppPersonal" style={{ width: 20, height: 20 }} />WhatsAppPersonal</span>, value: 'whatsapp_personal' },
    { label: <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><img src={lineIcon} alt="Line" style={{ width: 20, height: 20 }} />Line</span>, value: 'line' },
]

const countryOptions = [
    { label: 'Indonesia', value: 'Indonesia' },
    { label: 'Malaysia', value: 'Malaysia' },
    { label: 'United States of America', value: 'United States' },
    { label: 'Philippines', value: 'Philippines' },
    { label: 'China', value: 'China' },
    { label: 'Thailand', value: 'Thailand' },
    { label: 'Vietnam', value: 'Vietnam' },
    { label: 'Korea', value: 'South Korea' },
    { label: 'Japan', value: 'Japan' },
    { label: 'France', value: 'France' },
    { label: 'Bangladesh', value: 'Bangladesh' },
    { label: 'Mexico', value: 'Mexico' },
    { label: 'Columbia', value: 'Columbia' },
    { label: 'Brazil', value: 'Brazil' },
    { label: 'Spain', value: 'Spain' },
    { label: 'India', value: 'India' },
]

const timezoneOptions = [
    { label: 'UTC+01:00', value: 'UTC+01:00' },
    { label: 'UTC+05:30', value: 'UTC+05:30' },
    { label: 'UTC+06:00', value: 'UTC+06:00' },
    { label: 'UTC+07:00', value: 'UTC+07:00' },
    { label: 'UTC+08:00', value: 'UTC+08:00' },
    { label: 'UTC+09:00', value: 'UTC+09:00' },
    { label: 'UTC-3:00', value: 'UTC-3:00' },
    { label: 'UTC-4:00', value: 'UTC-4:00' },
    { label: 'UTC-5:00', value: 'UTC-5:00' },
    { label: 'UTC-6:00', value: 'UTC-6:00' },
    { label: 'UTC-7:00', value: 'UTC-7:00' },
    { label: 'UTC-8:00', value: 'UTC-8:00' },
    { label: 'UTC-9:00', value: 'UTC-9:00' },
    { label: 'UTC-10:00', value: 'UTC-10:00' },
]

interface AddChannelProps {
    open: boolean
    onCancel: () => void
    onSuccess: () => void
    /** WhatsApp 个人号创建成功后，打开扫码弹窗 */
    onWhatsappCreated?: (channelId: string) => void
}

const AddChannel: React.FC<AddChannelProps> = ({ open, onCancel, onSuccess, onWhatsappCreated }) => {
    const [form] = Form.useForm()
    const [loading, setLoading] = useState(false)
    const companyName = useSelector((state: any) => state.user.userInfo?.company_name)
    // WhatsApp 个人号：一次新增操作只生成一个稳定的 channel_id，超时重试沿用同一个，直到弹窗关闭或创建成功
    const waChannelIdRef = useRef<string | null>(null)

    useEffect(() => {
        if (!open) waChannelIdRef.current = null
    }, [open])

    const handleOk = async () => {
        try {
            const values = await form.validateFields()
            setLoading(true)
            if (values.type === 'telegram') {
                const res = await createTelegramChannelApi({
                    company: companyName || '',
                    country: values.country,
                    time_zone: values.time_zone,
                    token: values.token,
                })
                if (res.code === 0) {
                    message.success('Channel created successfully')
                    form.resetFields()
                    onSuccess()
                } else if (res.code === 111002) {
                    message.warning('Channel already exists, please use a different Token')
                } else {
                    message.error(res.message || 'Failed to create channel')
                }
            } else if (values.type === 'webwidget') {
                const res = await createWebwidgetChannelApi({
                    channel_id: values.channel_id,
                    channel_name: values.channel_name,
                    company: companyName || '',
                    country: values.country,
                    time_zone: values.time_zone,
                })
                if (res.code === 0) {
                    message.success('Channel created successfully')
                    form.resetFields()
                    onSuccess()
                } else if (res.code === 111002) {
                    message.warning('Channel already exists, please use a different Channel ID')
                } else {
                    message.error(res.message || 'Failed to create channel')
                }
            } else if (values.type === 'whatsapp_personal') {
                if (!companyName) {
                    message.error('Company is missing, please re-login')
                    return
                }
                if (!waChannelIdRef.current) waChannelIdRef.current = genWaChannelId()
                const channelId = waChannelIdRef.current
                try {
                    await createWaAccount({
                        channel_id: channelId,
                        display_name: values.display_name?.trim(),
                        company: companyName,
                        country: values.country,
                        time_zone: values.time_zone,
                        group_ids: [],
                        robot_id: 0,
                        robot_version_id: 0,
                        robot_name: '',
                    })
                    message.success('WhatsApp account created, please scan the QR code')
                    waChannelIdRef.current = null
                    form.resetFields()
                    onSuccess()
                    onWhatsappCreated?.(channelId)
                } catch (e: any) {
                    const tip = e?.requestId ? ` (request_id: ${e.requestId})` : ''
                    if (e?.uncertain) {
                        message.warning(`Create result is unknown, click Create again to retry with the same account ID${tip}`)
                    } else {
                        message.error(`${e?.message || 'Failed to create WhatsApp account'}${tip}`)
                    }
                }
            } else if (values.type === 'line') {
                const res = await createLineChannelApi({
                    company: companyName || '',
                    country: values.country,
                    time_zone: values.time_zone,
                    channel_id: values.channel_id,
                    channel_secret: values.channel_secret,
                })
                if (res.code === 0) {
                    message.success('Channel created successfully')
                    form.resetFields()
                    onSuccess()
                } else if (res.code === 111002) {
                    message.warning('Channel already exists, please use a different Channel ID')
                } else {
                    message.error(res.message || 'Failed to create channel')
                }
            }

        } catch {
            // validation failed
        } finally {
            setLoading(false)
        }
    }

    const handleCancel = () => {
        form.resetFields()
        onCancel()
    }

    return (
        <Modal
            title="Channel"
            open={open}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={loading}
            okText="Create"
            cancelText="Cancel"
            centered
            destroyOnClose
            width={700}
            className="add-channel-modal"
        >
            <ProForm
                name="channel-create"
                form={form}
                submitter={false}
                grid={true}
                layout="vertical"
                style={{ marginTop: 16 }}
            >
                <ProFormSelect
                    name="type"
                    label="Channel Type"
                    options={channelTypeOptions}
                    rules={[{ required: true, message: 'Please select Channel Type' }]}
                    className={styles.selectWrapper}
                    fieldProps={{
                        style: {
                            borderRadius: '10px',
                            backgroundColor: '#090A0D',
                            color: '#515766',
                            height: '40px',
                        },
                        dropdownStyle: {
                            background: '#1A1D24',
                            border: '1px solid #2A2D36',
                        },
                        allowClear: true,
                    }}
                />
                <ProFormDependency name={['type']}>
                    {({ type }) => {
                        if (type === 'webwidget') {
                            return (
                                <>
                                    <ProFormText
                                        name="channel_id"
                                        label="Channel ID"
                                        rules={[
                                            { required: true, message: 'Please enter Channel ID' },
                                            { pattern: /^[a-zA-Z0-9\-_]+$/, message: 'Only letters, numbers, - and _ are allowed' },
                                        ]}
                                        fieldProps={{
                                            style: {
                                                borderRadius: '10px',
                                                backgroundColor: '#090A0D',
                                                height: '40px',
                                            },
                                        }}
                                    />
                                    <ProFormText
                                        name="channel_name"
                                        label="Channel Name"
                                        rules={[
                                            { required: true, message: 'Please enter Channel Name' },
                                            { pattern: /^[a-zA-Z0-9\-_]+$/, message: 'Only letters, numbers, - and _ are allowed' },
                                        ]}
                                        fieldProps={{
                                            style: {
                                                borderRadius: '10px',
                                                backgroundColor: '#090A0D',
                                                height: '40px',
                                            },
                                        }}
                                    />
                                    <ProForm.Group>
                                        <ProFormSelect
                                            name="country"
                                            label="Country"
                                            showSearch
                                            colProps={{ span: 12 }}
                                            rules={[{ required: true, message: 'Please select Country' }]}
                                            options={countryOptions}
                                            className={styles.selectWrapper}
                                            fieldProps={{
                                                style: {
                                                    borderRadius: '10px',
                                                    backgroundColor: '#090A0D',
                                                    color: '#515766',
                                                    height: '40px',
                                                },
                                                dropdownStyle: {
                                                    background: '#1A1D24',
                                                    border: '1px solid #2A2D36',
                                                },
                                                allowClear: true,
                                            }}
                                        />
                                        <ProFormSelect
                                            name="time_zone"
                                            label="Time Zone"
                                            showSearch
                                            colProps={{ span: 12 }}
                                            rules={[{ required: true, message: 'Please select Time Zone' }]}
                                            options={timezoneOptions}
                                            className={styles.selectWrapper}
                                            fieldProps={{
                                                style: {
                                                    borderRadius: '10px',
                                                    backgroundColor: '#090A0D',
                                                    color: '#515766',
                                                    height: '40px',
                                                },
                                                dropdownStyle: {
                                                    background: '#1A1D24',
                                                    border: '1px solid #2A2D36',
                                                },
                                                allowClear: true,
                                            }}
                                        />
                                    </ProForm.Group>
                                </>
                            )
                        }
                        if (type === 'telegram') {
                            return (
                                <>
                                    <ProForm.Group>
                                        <ProFormSelect
                                            name="country"
                                            label="Country"
                                            showSearch
                                            colProps={{ span: 12 }}
                                            rules={[{ required: true, message: 'Please select Country' }]}
                                            options={countryOptions}
                                            className={styles.selectWrapper}
                                            fieldProps={{
                                                style: {
                                                    borderRadius: '10px',
                                                    backgroundColor: '#090A0D',
                                                    color: '#515766',
                                                    height: '40px',
                                                },
                                                dropdownStyle: {
                                                    background: '#1A1D24',
                                                    border: '1px solid #2A2D36',
                                                },
                                                allowClear: true,
                                            }}
                                        />
                                        <ProFormSelect
                                            name="time_zone"
                                            label="Time Zone"
                                            showSearch
                                            colProps={{ span: 12 }}
                                            rules={[{ required: true, message: 'Please select Time Zone' }]}
                                            options={timezoneOptions}
                                            className={styles.selectWrapper}
                                            fieldProps={{
                                                style: {
                                                    borderRadius: '10px',
                                                    backgroundColor: '#090A0D',
                                                    color: '#515766',
                                                    height: '40px',
                                                },
                                                dropdownStyle: {
                                                    background: '#1A1D24',
                                                    border: '1px solid #2A2D36',
                                                },
                                                allowClear: true,
                                            }}
                                        />
                                    </ProForm.Group>
                                    <ProFormText
                                        name="token"
                                        label="Telegram Token"
                                        rules={[{ required: true, message: 'Please enter Telegram Token' }]}
                                        fieldProps={{
                                            style: {
                                                borderRadius: '10px',
                                                backgroundColor: '#090A0D',
                                                height: '40px',
                                            },
                                        }}
                                    />
                                </>
                            )
                        }
                        if (type === 'whatsapp_personal') {
                            return (
                                <>
                                    <ProFormText
                                        name="display_name"
                                        label="Display Name"
                                        rules={[
                                            { required: true, whitespace: true, message: 'Please enter Display Name' },
                                            { max: 255, message: 'At most 255 characters' },
                                        ]}
                                        fieldProps={{
                                            style: {
                                                borderRadius: '10px',
                                                backgroundColor: '#090A0D',
                                                height: '40px',
                                            },
                                        }}
                                    />
                                    <ProForm.Group>
                                        <ProFormSelect
                                            name="country"
                                            label="Country"
                                            showSearch
                                            colProps={{ span: 12 }}
                                            rules={[{ required: true, message: 'Please select Country' }]}
                                            options={countryOptions}
                                            className={styles.selectWrapper}
                                            fieldProps={{
                                                style: {
                                                    borderRadius: '10px',
                                                    backgroundColor: '#090A0D',
                                                    color: '#515766',
                                                    height: '40px',
                                                },
                                                dropdownStyle: {
                                                    background: '#1A1D24',
                                                    border: '1px solid #2A2D36',
                                                },
                                                allowClear: true,
                                            }}
                                        />
                                        <ProFormSelect
                                            name="time_zone"
                                            label="Time Zone"
                                            showSearch
                                            colProps={{ span: 12 }}
                                            rules={[{ required: true, message: 'Please select Time Zone' }]}
                                            options={timezoneOptions}
                                            className={styles.selectWrapper}
                                            fieldProps={{
                                                style: {
                                                    borderRadius: '10px',
                                                    backgroundColor: '#090A0D',
                                                    color: '#515766',
                                                    height: '40px',
                                                },
                                                dropdownStyle: {
                                                    background: '#1A1D24',
                                                    border: '1px solid #2A2D36',
                                                },
                                                allowClear: true,
                                            }}
                                        />
                                    </ProForm.Group>
                                    <div style={{ color: '#717787', fontSize: 12, marginTop: -8 }}>
                                        After creation, scan the QR code with WhatsApp (Linked devices), then select groups and a bot.
                                    </div>
                                </>
                            )
                        }
                        if (type === 'line') {
                            return (
                                <>
                                    <ProFormText
                                        name="channel_id"
                                        label="Channel ID"
                                        rules={[
                                            { required: true, message: 'Please enter Channel ID' },
                                        ]}
                                        fieldProps={{
                                            style: {
                                                borderRadius: '10px',
                                                backgroundColor: '#090A0D',
                                                height: '40px',
                                            },
                                        }}
                                    />
                                    <ProFormText
                                        name="channel_secret"
                                        label="Channel Secret"
                                        rules={[
                                            { required: true, message: 'Please enter Channel Secret' },
                                        ]}
                                        fieldProps={{
                                            style: {
                                                borderRadius: '10px',
                                                backgroundColor: '#090A0D',
                                                height: '40px',
                                            },
                                        }}
                                    />
                                    <ProForm.Group>
                                        <ProFormSelect
                                            name="country"
                                            label="Country"
                                            showSearch
                                            colProps={{ span: 12 }}
                                            rules={[{ required: true, message: 'Please select Country' }]}
                                            options={countryOptions}
                                            className={styles.selectWrapper}
                                            fieldProps={{
                                                style: {
                                                    borderRadius: '10px',
                                                    backgroundColor: '#090A0D',
                                                    color: '#515766',
                                                    height: '40px',
                                                },
                                                dropdownStyle: {
                                                    background: '#1A1D24',
                                                    border: '1px solid #2A2D36',
                                                },
                                                allowClear: true,
                                            }}
                                        />
                                        <ProFormSelect
                                            name="time_zone"
                                            label="Time Zone"
                                            showSearch
                                            colProps={{ span: 12 }}
                                            rules={[{ required: true, message: 'Please select Time Zone' }]}
                                            options={timezoneOptions}
                                            className={styles.selectWrapper}
                                            fieldProps={{
                                                style: {
                                                    borderRadius: '10px',
                                                    backgroundColor: '#090A0D',
                                                    color: '#515766',
                                                    height: '40px',
                                                },
                                                dropdownStyle: {
                                                    background: '#1A1D24',
                                                    border: '1px solid #2A2D36',
                                                },
                                                allowClear: true,
                                            }}
                                        />
                                    </ProForm.Group>
                                </>
                            )
                        }
                        return null
                    }}
                </ProFormDependency>
            </ProForm>
        </Modal>
    )
}

export default AddChannel
